import { useState } from 'react';
import { SettingsModal } from '../SettingsModal';
import { validateCsvCallback, validateImageOrPdfCallback } from '../../../../utils/validateFiles';
import {
  TProductConfigResponse,
  CONFIGURATION_CLASS_TYPE,
  CONFIGURATION_STATUS_MODAL_VALUES,
  CONFIG_ADDITIONAL_ACTION_ID,
  CONFIG_STATUS_NAME,
  CONFIG_STATUS_ID,
  LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID,
  CONFIG_EVIDENCE_FIELD_NAME_ID
} from '../../../../types/product.configuration.types';
import { getConfigurationDisplayElements } from '../../../../utils/logisticServices.utils';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { updateConfigurationStatus } from '../../../../services/product-configuration.service';
import { NudosLinkTypeButton, NudosUploadDownloadButton } from '../../../NudosComponents';
import NudosRadioButton from '../../../NudosRadioButton/NudosRadioButton';
import ConfigurationStatusModal from '../ConfigurationStatusModal/ConfigurationStatusModal';
import EnrollmentStatusIndicator from './EnrollmentStatusIndicator/EnrollmentStatusIndicator';
import {
  getConfigClass,
  getAdditionalAction,
  getFileUrlsFromEvidence,
  getNotCompletedReasonIdFromEvidence,
  getCommentsFromEvidence
} from '../../../../utils/configurations';
import { ResellerSearchBar } from './ResellerSearchBar/ResellerSearchBar';

import './ConfigurationStatusSection.scss';

const ConfigurationStatusSection = ({
  configurationData,
  updateDataCallback,
  countryId,
  countryCode,
  orderId,
  logisticServiceId
}: {
  configurationData: TProductConfigResponse;
  updateDataCallback: () => Promise<void>;
  countryId?: number | null;
  countryCode?: string | null;
  orderId?: string | null;
  logisticServiceId?: number;
}) => {
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [changeStatusModalType, setChangeStatusModalType] = useState<CONFIGURATION_STATUS_MODAL_VALUES>();
  const [loadingProofFile, setLoadingProofFile] = useState<boolean>(false);

  const {
    uuid: productUuid,
    status,
    evidence,
    data,
    configClassId,
    configClassName,
    additionalActions,
    reseller,
    isExternalService
  } = configurationData;

  const imageUrls = getFileUrlsFromEvidence(evidence);
  const notCompletedReasonId = getNotCompletedReasonIdFromEvidence(evidence);
  const comments = getCommentsFromEvidence(evidence);
  const statusId = status.statusId;

  const statusIsNotCompleted = statusId === CONFIG_STATUS_ID.NOT_COMPLETED;
  const configurationIsHashId = configClassName === CONFIGURATION_CLASS_TYPE.HASH_ID;
  const configurationIsCreateAdmin = configClassName === CONFIGURATION_CLASS_TYPE.CREATE_ADMIN;
  const isDEPLaptopsCategory = configClassName === CONFIGURATION_CLASS_TYPE.DEP;

  const getImageUrlsToShow = () => {
    if (isExternalService) return [];
    if (configurationIsHashId && !statusIsNotCompleted) return imageUrls?.slice(-2);
    return imageUrls?.slice(-1);
  };

  const imageUrlToShow = getImageUrlsToShow();
  const { text } = getConfigurationDisplayElements(configurationData);

  const configurationStatuses = [
    {
      statusName: CONFIG_STATUS_NAME.TO_COMPLETE,
      isActive: statusId === CONFIG_STATUS_ID.TO_COMPLETE,
      clickAction: undefined
    },
    {
      statusName: CONFIG_STATUS_NAME.COMPLETED,
      isActive: statusId === CONFIG_STATUS_ID.COMPLETED,
      clickAction: () => !isExternalService && setChangeStatusModalType(CONFIGURATION_STATUS_MODAL_VALUES.COMPLETED)
    },
    {
      statusName: CONFIG_STATUS_NAME.NOT_COMPLETED,
      isActive: statusId === CONFIG_STATUS_ID.NOT_COMPLETED,
      clickAction: () => !isExternalService && setChangeStatusModalType(CONFIGURATION_STATUS_MODAL_VALUES.NOT_COMPLETED)
    }
  ];

  const enrollmentAction = getAdditionalAction(additionalActions, CONFIG_ADDITIONAL_ACTION_ID.CONFIRM_ENROLLMENT);
  const shouldShowEnrollmentIndicator = !!enrollmentAction && !!additionalActions?.length;
  const isResellerSearchBarAlone = isDEPLaptopsCategory && !shouldShowEnrollmentIndicator;

  const openCommentsModal = () => setShowCommentsModal(true);
  const closeCommentsModal = () => setShowCommentsModal(false);
  const closeChangeStatusModal = () => setChangeStatusModalType(undefined);

  const buildConfigEvidence = () => {
    if (!notCompletedReasonId) return undefined;
    const isOtherReason = notCompletedReasonId === LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID.OTHER;
    const items = [
      { fieldNameId: CONFIG_EVIDENCE_FIELD_NAME_ID.NOT_COMPLETED_REASON, fieldValue: String(notCompletedReasonId) }
    ];
    if (isOtherReason && comments) {
      items.push({ fieldNameId: CONFIG_EVIDENCE_FIELD_NAME_ID.COMMENTS, fieldValue: comments });
    }
    return items;
  };

  const uploadProofFile = async (file?: File) => {
    if (!productUuid || !statusId) {
      return displayErrorNotification({ customJSXMessage: <>Funcionalidad aún no implementada</> });
    }
    if (!file) {
      return displayErrorNotification({ customJSXMessage: <>No se cargó ninguna imagen</> });
    }
    if (statusId === CONFIG_STATUS_ID.NOT_COMPLETED && !notCompletedReasonId) return displayErrorNotification();
    try {
      setLoadingProofFile(true);
      await updateConfigurationStatus(
        { uuid: productUuid, configStatusId: statusId, configEvidence: buildConfigEvidence() },
        [file]
      );
      await updateDataCallback();
      displaySuccessNotification({ customJSXMessage: <>Configuración actualizada con éxito</> });
    } catch {
      displayErrorNotification({ customJSXMessage: <>Error al actualizar la configuración</> });
    } finally {
      setLoadingProofFile(false);
    }
  };

  return (
    <div className="configurationStatusSection">
      <div className="nameAndComments">
        <div className="serviceName">{text}</div>
        {!!data && <NudosLinkTypeButton hideIcon text="Ver configuración" handleClickCallback={openCommentsModal} />}
      </div>
      <div className="statuses">
        {configurationStatuses.map(configStatus => {
          const { clickAction, statusName: thisStatusName, isActive } = configStatus;
          const isNotCompleted = thisStatusName === CONFIG_STATUS_NAME.NOT_COMPLETED;
          const isCompleted = thisStatusName === CONFIG_STATUS_NAME.COMPLETED;
          const isPending = thisStatusName === CONFIG_STATUS_NAME.TO_COMPLETE;
          const externalStyle = isExternalService ? 'external' : '';
          const disabledStyle = isPending || isActive ? 'disabled' : '';
          const notCompletedStyle = isNotCompleted ? `notCompleted notCompletedConfig` : '';
          const displayViewReasonsButton = (isNotCompleted || (isCompleted && configurationIsCreateAdmin)) && isActive;

          const seeNotCompletedReasons = () => {
            if (isNotCompleted) {
              return setChangeStatusModalType(CONFIGURATION_STATUS_MODAL_VALUES.SEE_NOT_COMPLETED_REASONS);
            }
            if (configurationIsCreateAdmin) {
              return setChangeStatusModalType(CONFIGURATION_STATUS_MODAL_VALUES.COMPLETED);
            }
          };

          return (
            <div
              className={`statusIndicator ${externalStyle} ${notCompletedStyle} ${disabledStyle}`}
              key={thisStatusName}
            >
              <NudosRadioButton
                componentSize="small"
                isButtonActive={isActive}
                handleClickButton={isActive ? undefined : clickAction}
              />
              <div className="statusName">{thisStatusName}</div>
              {displayViewReasonsButton && (
                <NudosLinkTypeButton hideIcon text={'Ver motivos'} handleClickCallback={seeNotCompletedReasons} />
              )}
            </div>
          );
        })}
      </div>

      <div className="evidenceContainer">
        {imageUrlToShow?.map((imageUrl, index) => {
          const fileExtension = imageUrl?.split('.').pop()?.toLowerCase();
          const isCsv = fileExtension === 'csv';
          const buttonText = isCsv ? 'CSV' : 'PDF o JPG';
          const validateFileCallback = isCsv ? validateCsvCallback : validateImageOrPdfCallback;
          return (
            <NudosUploadDownloadButton
              key={`imageUrl-${index}`}
              customClassName="updateProofButton updateProofButtonOrders"
              customWidth="127px"
              customHeight="30px"
              currentFileUrl={imageUrl}
              uploadedFileHandler={uploadProofFile}
              downloadButtonText="Ver evidencia"
              buttonEmptyText={buttonText}
              buttonFilledText={buttonText}
              showSkeletonLoader={loadingProofFile}
              keepOwnFileState
              validateFileCallback={validateFileCallback}
            />
          );
        })}
      </div>

      <div className={`enrollmentAndResellerRow ${isResellerSearchBarAlone ? 'resellerOnly' : ''}`}>
        {shouldShowEnrollmentIndicator && (
          <EnrollmentStatusIndicator enrollmentAction={enrollmentAction} onConfirmEnrollment={updateDataCallback} />
        )}

        {isDEPLaptopsCategory && (
          <ResellerSearchBar
            reseller={reseller}
            countryCode={countryCode}
            countryId={countryId}
            productUuid={productUuid}
            onResellerSelect={updateDataCallback}
          />
        )}
      </div>

      {showCommentsModal && (
        <SettingsModal
          closeModal={closeCommentsModal}
          configClass={getConfigClass(configClassId)}
          configData={data || []}
        />
      )}
      {changeStatusModalType && (
        <ConfigurationStatusModal
          type={changeStatusModalType}
          closeModalCallback={closeChangeStatusModal}
          updateDataCallback={updateDataCallback}
          configurationData={configurationData}
          orderId={orderId}
          logisticServiceId={logisticServiceId}
          productUuid={productUuid}
        />
      )}
    </div>
  );
};

export default ConfigurationStatusSection;
